import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  Image,
  Dimensions,
  Alert,
  ActivityIndicator,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { Ionicons, FontAwesome } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as LocalAuthentication from 'expo-local-authentication';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

const { width, height: screenHeight } = Dimensions.get('window');

const UserTypeSelectionScreen = ({ navigation }) => {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const [isLoading, setIsLoading] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [savedCredentials, setSavedCredentials] = useState({
    business: false,
    professional: false,
    learning: false
  });

  useEffect(() => {
    checkBiometricAvailability();
    checkSavedCredentials();
  }, []);

  const checkBiometricAvailability = async () => {
    try {
      const hasHardware = await LocalAuthentication.hasHardwareAsync();
      const isEnrolled = await LocalAuthentication.isEnrolledAsync();
      setBiometricAvailable(hasHardware && isEnrolled);
    } catch (error) {
      console.error('Error checking biometric availability:', error);
    }
  };

  const checkSavedCredentials = async () => {
    try {
      const businessCreds = await AsyncStorage.getItem('biometric_credentials_business');
      const professionalCreds = await AsyncStorage.getItem('biometric_credentials_professional');
      const learningCreds = await AsyncStorage.getItem('biometric_credentials_learning');
      
      setSavedCredentials({
        business: !!businessCreds,
        professional: !!professionalCreds,
        learning: !!learningCreds
      });
    } catch (error) {
      console.error('Error checking saved credentials:', error);
    }
  };

  const userTypes = [
    {
      type: 'professional',
      title: 'I\'m a Professional',
      subtitle: 'Looking for engineering opportunities',
      description: 'Find your dream job, build your career, connect with top employers',
      icon: 'person-outline',
      color: '#007AFF',
      gradient: ['#007AFF', '#0056CC'],
      features: ['Job Search', 'Career Development', 'Skill Assessment', 'Resume Builder'],
      loginScreen: 'ProfessionalLogin',
      signupScreen: 'ProfessionalSignup'
    },
    {
      type: 'business',
      title: 'I\'m an Employer',
      subtitle: 'Hiring engineering talent',
      description: 'Post jobs, find candidates, manage recruitment process',
      icon: 'business-outline',
      color: '#FF9500',
      gradient: ['#FF9500', '#CC7700'],
      features: ['Post Jobs', 'Find Candidates', 'Recruitment Tools', 'Analytics'],
      loginScreen: 'BusinessLogin',
      signupScreen: 'BusinessSignup'
    },
    {
      type: 'learning',
      title: 'I\'m a Learner',
      subtitle: 'Developing engineering skills',
      description: 'Access courses, earn certificates, track your progress',
      icon: 'school-outline',
      color: '#34C759',
      gradient: ['#34C759', '#28A745'],
      features: ['Online Courses', 'Certifications', 'Progress Tracking', 'Career Guidance'],
      loginScreen: 'LearningLoginScreen',
      signupScreen: 'LearningSignup'
    }
  ];

  const handleUserTypeSelect = (userType) => {
    const selectedType = userTypes.find(type => type.type === userType);
    
    Alert.alert(
      `Welcome to ${selectedType.title.replace("I'm ", "")} Portal`,
      'How would you like to get started?',
      [
        {
          text: 'Sign Up',
          onPress: () => {
            // Navigate to RegisterScreen with userType parameter
            navigation.navigate('RegisterScreen', { userType: userType });
          },
          style: 'default'
        },
        {
          text: 'Sign In',
          onPress: () => navigation.navigate(selectedType.loginScreen),
          style: 'default'
        },
        {
          text: 'Cancel',
          style: 'cancel'
        }
      ]
    );
  };

  const handleQuickSignUp = (userType) => {
    // Navigate directly to RegisterScreen with userType
    navigation.navigate('RegisterScreen', { userType: userType });
  };

  const handleQuickLogin = (userType) => {
    const selectedType = userTypes.find(type => type.type === userType);
    navigation.navigate(selectedType.loginScreen);
  };

  const handleBiometricQuickLogin = async (userType) => {
    if (!biometricAvailable) {
      Alert.alert('Unavailable', 'Biometric authentication is not available on this device');
      return;
    }

    if (!savedCredentials[userType]) {
      Alert.alert('No Saved Credentials', `Please login to your ${userType} account first to enable biometric login`);
      return;
    }

    try {
      setIsLoading(true);
      
      const result = await LocalAuthentication.authenticateAsync({
        promptMessage: `Login to your ${userType} account`,
        cancelLabel: 'Cancel',
        fallbackLabel: 'Use Password',
      });

      if (result.success) {
        // Load saved credentials and navigate directly
        const savedCredentials = await AsyncStorage.getItem(`biometric_credentials_${userType}`);
        if (savedCredentials) {
          const credentials = JSON.parse(savedCredentials);
          
          // Restore user session
          await AsyncStorage.setItem('auth_token', credentials.token);
          const userData = {
            id: credentials.user_id,
            email: credentials.email,
            name: credentials.name,
            user_type: userType,
            loginMethod: 'biometric'
          };
          await AsyncStorage.setItem('user_data', JSON.stringify(userData));
          
          // Navigate to appropriate welcome screen
          switch (userType) {
            case 'business':
              navigation.navigate('WelcomeBusiness');
              break;
            case 'professional':
              navigation.navigate('WelcomeProfessionalScreen');
              break;
            case 'learning':
              navigation.navigate('WelcomeLearningScreen');
              break;
          }
        }
      }
    } catch (error) {
      console.error('Biometric login error:', error);
      Alert.alert('Authentication Error', 'Please try again or use regular login');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView 
        style={styles.keyboardAvoidingView}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView 
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={true}
          bounces={true}
          alwaysBounceVertical={true}
          scrollEventThrottle={16}
        >
          {/* Header */}
          <View style={styles.header}>
            <Image
              source={require('../../../assets/jobeng_full_logo.png')}
              style={styles.logo}
              resizeMode="contain"
            />
            <Text style={styles.title}>Welcome to JobEng</Text>
            <Text style={styles.subtitle}>UK Engineering Excellence Platform</Text>
            <Text style={styles.description}>Choose your path to get started</Text>
          </View>

          {/* User Types Container */}
          <View style={styles.userTypesContainer}>
            {userTypes.map((userType, index) => (
              <TouchableOpacity
                key={userType.type}
                style={[styles.userTypeCard, { borderColor: userType.color }]}
                onPress={() => handleUserTypeSelect(userType.type)}
                activeOpacity={0.8}
              >
                <View style={styles.cardHeader}>
                  <View style={[styles.iconContainer, { backgroundColor: userType.color }]}>
                    <Ionicons name={userType.icon} size={32} color="#fff" />
                  </View>
                  <View style={styles.cardTitleContainer}>
                    <Text style={styles.cardTitle}>{userType.title}</Text>
                    <Text style={styles.cardSubtitle}>{userType.subtitle}</Text>
                  </View>
                  
                  {/* Quick Login Options */}
                  <View style={styles.quickLoginContainer}>
                    {savedCredentials[userType.type] && biometricAvailable && (
                      <TouchableOpacity
                        style={[styles.quickLoginButton, { backgroundColor: userType.color }]}
                        onPress={() => handleBiometricQuickLogin(userType.type)}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <ActivityIndicator size={20} color="#FFFFFF" />
                        ) : (
                          <Ionicons name="finger-print" size={16} color="#FFFFFF" />
                        )}
                      </TouchableOpacity>
                    )}
                    
                    <TouchableOpacity
                      style={[styles.quickLoginButton, { backgroundColor: userType.color + '20', borderColor: userType.color }]}
                      onPress={() => handleQuickLogin(userType.type)}
                    >
                      <Ionicons name="log-in-outline" size={16} color={userType.color} />
                    </TouchableOpacity>
                  </View>
                </View>
                
                <Text style={styles.cardDescription}>{userType.description}</Text>
                
                <View style={styles.featuresContainer}>
                  {userType.features.map((feature, idx) => (
                    <View key={idx} style={styles.featureItem}>
                      <Ionicons name="checkmark-circle" size={16} color={userType.color} />
                      <Text style={styles.featureText}>{feature}</Text>
                    </View>
                  ))}
                </View>
                
                <View style={styles.actionButtonsContainer}>
                  <TouchableOpacity
                    style={[styles.actionButton, styles.signUpButton, { backgroundColor: userType.color }]}
                    onPress={() => handleQuickSignUp(userType.type)}
                  >
                    <Text style={styles.signUpButtonText}>Sign Up</Text>
                    <Ionicons name="person-add" size={16} color="#fff" />
                  </TouchableOpacity>
                  
                  <TouchableOpacity
                    style={[styles.actionButton, styles.signInButton, { borderColor: userType.color }]}
                    onPress={() => handleQuickLogin(userType.type)}
                  >
                    <Text style={[styles.signInButtonText, { color: userType.color }]}>Sign In</Text>
                    <Ionicons name="log-in-outline" size={16} color={userType.color} />
                  </TouchableOpacity>
                </View>
              </TouchableOpacity>
            ))}
          </View>

          {/* Authentication Options */}
          <View style={styles.authOptionsContainer}>
            <Text style={styles.authOptionsTitle}>Quick Access</Text>
            
            <View style={styles.authButtonsContainer}>
              <TouchableOpacity
                style={styles.authButton}
                onPress={() => navigation.navigate('BusinessLogin')}
              >
                <FontAwesome name="briefcase" size={16} color="#FF9500" />
                <Text style={styles.authButtonText}>Business Login</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={styles.authButton}
                onPress={() => navigation.navigate('ProfessionalLogin')}
              >
                <FontAwesome name="user-tie" size={16} color="#007AFF" />
                <Text style={styles.authButtonText}>Professional Login</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={styles.authButton}
                onPress={() => navigation.navigate('LearningLoginScreen')}
              >
                <FontAwesome name="graduation-cap" size={16} color="#34C759" />
                <Text style={styles.authButtonText}>Learning Login</Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Footer */}
          <View style={styles.footer}>
            <Text style={styles.footerText}>
              New to JobEng?{' '}
              <Text 
                style={styles.footerLink}
                onPress={() => navigation.navigate('RegisterScreen', { userType: 'professional' })}
              >
                Create an account
              </Text>
            </Text>
            
            {biometricAvailable && (savedCredentials.business || savedCredentials.professional || savedCredentials.learning) && (
              <View style={styles.biometricInfo}>
                <Ionicons name="finger-print" size={16} color="#007AFF" />
                <Text style={styles.biometricInfoText}>
                  Biometric login available for saved accounts
                </Text>
              </View>
            )}
          </View>

          {/* Extra space to ensure scrolling */}
          <View style={styles.extraSpace} />
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F2F2F7',
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 100,
    minHeight: screenHeight + 300, // Force content to be taller than screen
  },
  header: {
    padding: 20,
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  logo: {
    width: 120,
    height: 60,
    marginBottom: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    color: '#007AFF',
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
  },
  description: {
    fontSize: 16,
    color: '#8E8E93',
    textAlign: 'center',
    paddingHorizontal: 20,
  },
  userTypesContainer: {
    padding: 20,
  },
  userTypeCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    borderWidth: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  iconContainer: {
    width: 60,
    height: 60,
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  cardTitleContainer: {
    flex: 1,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  cardSubtitle: {
    fontSize: 14,
    color: '#8E8E93',
    marginTop: 2,
  },
  quickLoginContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  quickLoginButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 8,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  cardDescription: {
    fontSize: 14,
    color: '#555555',
    marginBottom: 16,
    lineHeight: 20,
  },
  featuresContainer: {
    marginBottom: 16,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  featureText: {
    fontSize: 14,
    color: '#555555',
    marginLeft: 8,
  },
  actionButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
  },
  signUpButton: {
    // backgroundColor set dynamically
  },
  signUpButtonText: {
    color: '#FFFFFF',
    fontWeight: '600',
    marginRight: 8,
    fontSize: 16,
  },
  signInButton: {
    backgroundColor: 'transparent',
    borderWidth: 2,
    // borderColor set dynamically
  },
  signInButtonText: {
    fontWeight: '600',
    marginRight: 8,
    fontSize: 16,
    // color set dynamically
  },
  authOptionsContainer: {
    backgroundColor: '#FFFFFF',
    marginHorizontal: 20,
    marginBottom: 20,
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  authOptionsTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 16,
    textAlign: 'center',
  },
  authButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  authButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F2F2F7',
    borderRadius: 12,
    padding: 12,
    marginHorizontal: 4,
    borderWidth: 1,
    borderColor: '#E5E5EA',
  },
  authButtonText: {
    fontSize: 12,
    color: '#1C1C1E',
    fontWeight: '500',
    marginLeft: 6,
  },
  footer: {
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  footerText: {
    fontSize: 14,
    color: '#8E8E93',
    textAlign: 'center',
    marginBottom: 12,
  },
  footerLink: {
    color: '#007AFF',
    fontWeight: '500',
  },
  biometricInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E3F2FD',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
  },
  biometricInfoText: {
    fontSize: 12,
    color: '#007AFF',
    marginLeft: 6,
    fontWeight: '500',
  },
  extraSpace: {
    height: 100,
  },
});

export default UserTypeSelectionScreen;